import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import HomeIcon from '@mui/icons-material/Home';
import { useCurrency } from '../../context/CurrencyContext';

const PropertyDetails = ({ property, unit }) => {
  const { formatMoney } = useCurrency();
  if (!property && !unit) {
    return (
      <Paper sx={{ p: 3 }}>
        <Typography variant="h6">No property assigned</Typography>
        <Typography color="textSecondary">
          Please contact your property manager to be assigned a property.
        </Typography>
      </Paper>
    );
  }

  // Use unit data if available, otherwise fall back to property
  const displayProperty = unit?.property || property;
  const monthlyRent = unit?.monthlyRent || property?.monthlyRent;
  const bedrooms = unit?.bedrooms || property?.bedrooms;
  const bathrooms = unit?.bathrooms || property?.bathrooms;
  const squareFeet = unit?.squareFeet || property?.squareFeet;
  const deposit = unit?.deposit || property?.deposit;
  const amenities = unit?.amenities || property?.amenities;
  const description = unit?.description || property?.description;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Property Details
      </Typography>
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" mb={2}>
                <HomeIcon sx={{ mr: 1 }} />
                <Typography variant="h6">
                  {displayProperty?.propertyName || 'Property'}
                  {unit && ` - ${unit.unitNumber}`}
                </Typography>
                <Chip
                  label={unit?.status || displayProperty?.status || 'N/A'}
                  color={
                    (unit?.status || displayProperty?.status) === 'occupied'
                      ? 'success'
                      : (unit?.status || displayProperty?.status) === 'available'
                      ? 'primary'
                      : 'warning'
                  }
                  sx={{ ml: 2 }}
                />
              </Box>
              <Box display="flex" alignItems="center" mb={2}>
                <LocationOnIcon sx={{ mr: 1 }} />
                <Typography>
                  {displayProperty?.addressStreet}
                  {displayProperty?.address?.country && `, ${displayProperty.address.country}`}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="textSecondary">
                Property Type
              </Typography>
              <Typography variant="h6">
                {displayProperty?.propertyType === 'commercial' ? 'Commercial' : 'Residential'}
                {unit && ' - Unit'}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {monthlyRent !== undefined && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Monthly Rent
                </Typography>
                <Typography variant="h6">{formatMoney(monthlyRent, { decimals: 0 })}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {bedrooms !== undefined && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Bedrooms
                </Typography>
                <Typography variant="h6">{bedrooms}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {bathrooms !== undefined && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Bathrooms
                </Typography>
                <Typography variant="h6">{bathrooms}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {squareFeet !== undefined && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Square Feet
                </Typography>
                <Typography variant="h6">{squareFeet?.toLocaleString()} sq ft</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {deposit !== undefined && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary">
                  Deposit
                </Typography>
                <Typography variant="h6">{formatMoney(deposit, { decimals: 0 })}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
        {amenities && amenities.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Amenities
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1}>
                  {amenities.map((amenity, index) => (
                    <Chip key={index} label={amenity} size="small" />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}
        {description && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                  Description
                </Typography>
                <Typography>{description}</Typography>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default PropertyDetails;

